'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, RotateCcw, AlertTriangle, ShieldCheck, Check, Info } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface Question {
  id: number;
  text: string;
  options: {
    label: string;
    points: number;
    description?: string;
  }[];
}

const QUESTIONS: Question[] = [
  {
    id: 1,
    text: "How active is your family's voice footprint online?",
    options: [
      {
        label: "Highly active",
        points: 30,
        description: "Public profiles, regular posts, stories or TikTok/YouTube videos"
      },
      {
        label: "Moderate",
        points: 20,
        description: "Private accounts, send voice notes in WhatsApp/iMessage groups"
      },
      {
        label: "Low or None",
        points: 5,
        description: "Rarely share audio or video on public or social channels"
      }
    ]
  },
  {
    id: 2,
    text: "Do your parents or elderly relatives know what an 'AI voice clone scam' is?",
    options: [
      {
        label: "Never heard of it",
        points: 30,
        description: "They are completely unaware that caller voices can be forged"
      },
      {
        label: "Heard of it, but unprepared",
        points: 15,
        description: "They know it exists but don't know how to verify or handle it"
      },
      {
        label: "Fully aware & prepared",
        points: 0,
        description: "We have discussed this and established security verifications"
      }
    ]
  },
  {
    id: 3,
    text: "If they got a call in your voice asking for money due to an emergency, how would they react?",
    options: [
      {
        label: "Comply immediately",
        points: 40,
        description: "Panic would take over; they would wire funds or buy gift cards fast"
      },
      {
        label: "Hesitate, but probably comply",
        points: 20,
        description: "They might feel suspicious but comply anyway due to the urgency"
      },
      {
        label: "Hang up & verify",
        points: 0,
        description: "They would immediately hang up and call your verified number"
      }
    ]
  },
  {
    id: 4,
    text: "Does your family have a secret 'safe-word' or safety protocol?",
    options: [
      {
        label: "No protocol",
        points: 20,
        description: "We have no codes, no shared safe words, and no verification plan"
      },
      {
        label: "Informal plan",
        points: 10,
        description: "We mentioned safe words once, but never practiced or verified them"
      },
      {
        label: "Established plan",
        points: 0,
        description: "We have an active protocol, clear emergency steps, and a safe word"
      }
    ]
  }
];

export function FamilyRiskQuiz() {
  const [currentStep, setCurrentStep] = useState<number>(-1); // -1 = start, 0..3 = questions, 4 = results
  const [answers, setAnswers] = useState<number[]>([]);
  const [displayScore, setDisplayScore] = useState<number>(0);
  const [isInView, setIsInView] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleStart = () => {
    setCurrentStep(0);
    setAnswers([]);
    trackEvent('risk_quiz_started');
  };

  const handleAnswerSelect = (points: number) => {
    const nextAnswers = [...answers, points];
    setAnswers(nextAnswers);
    
    trackEvent('risk_quiz_question_answered', {
      question_id: currentStep + 1,
      points_selected: points,
    });

    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(QUESTIONS.length);
      // Calculate and animate score
      const totalPoints = nextAnswers.reduce((a, b) => a + b, 0);
      // Max possible is 30 + 30 + 40 + 20 = 120. Normalize to percentage.
      const rawPercentage = Math.round((totalPoints / 120) * 100);
      const finalPercentage = Math.max(10, Math.min(99, rawPercentage)); // Clamp 10-99%
      
      trackEvent('risk_quiz_completed', {
        vulnerability_score: finalPercentage
      });

      // Animate score counter
      let start = 0;
      const duration = 1200;
      const startTime = performance.now();

      function animate(now: number) {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Easing out easeOutQuad
        const eased = progress * (2 - progress);
        setDisplayScore(Math.round(eased * finalPercentage));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      }
      requestAnimationFrame(animate);
    }
  };

  const handleReset = () => {
    setCurrentStep(-1);
    setAnswers([]);
    setDisplayScore(0);
  };

  const getRiskDetails = (score: number) => {
    if (score >= 75) {
      return {
        level: "Critical Risk",
        color: "#DC2626", // red
        bg: "#FEF2F2",
        border: "rgba(220, 38, 38, 0.2)",
        description: "Your family's online voice footprint combined with lack of protocols makes you extremely vulnerable to AI voice clone scams. A criminal could easily exploit your parents' panic.",
      };
    } else if (score >= 45) {
      return {
        level: "High Risk",
        color: "#EA580C", // orange
        bg: "#FFF7ED",
        border: "rgba(234, 88, 12, 0.2)",
        description: "There are notable vulnerabilities in your family's safety readiness. While you might have some awareness, the pressure of a real clone call could cause immediate compliance.",
      };
    } else {
      return {
        level: "Moderate Risk",
        color: "#CA8A04", // yellow-gold
        bg: "#FEFCE8",
        border: "rgba(202, 138, 4, 0.2)",
        description: "You have a solid awareness baseline, but there are still minor gaps. Creating a formal code and running a simulation will close these gaps entirely.",
      };
    }
  };

  const risk = getRiskDetails(displayScore);

  const radius = 64;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (displayScore / 100) * circumference;

  return (
    <div
      ref={ref}
      style={{
        maxWidth: 720,
        margin: '40px auto 0',
        borderRadius: 24,
        background: 'white',
        border: '1px solid #E7E5E4',
        boxShadow: '0 10px 30px rgba(28, 25, 23, 0.05)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '28px 24px',
          background: '#FAFAF7',
          borderBottom: '1px solid #E7E5E4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#1C1917',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Info size={18} className="text-[#004322]" />
            Family AI Clone Vulnerability Checkup
          </h3>
          <p style={{ fontSize: '0.8125rem', color: '#57534E', marginTop: 2 }}>
            Evaluate your household risk in 60 seconds
          </p>
        </div>
        {currentStep >= 0 && currentStep < QUESTIONS.length && (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: '#004322',
              background: 'rgba(34, 197, 94, 0.1)',
              padding: '4px 10px',
              borderRadius: 999,
              letterSpacing: 0.5,
            }}
          >
            Q {currentStep + 1} of {QUESTIONS.length}
          </span>
        )}
      </div>

      <div style={{ minHeight: 340, padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <AnimatePresence mode="wait">
          {currentStep === -1 && (
            <motion.div
              key="start"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{ textAlign: 'center' }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#F0FDF4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#004322',
                }}
              >
                <AlertTriangle size={32} />
              </div>
              <h4
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.375rem',
                  fontWeight: 700,
                  color: '#1C1917',
                  marginBottom: 12,
                }}
              >
                Are your parents vulnerable to AI voice clone scams?
              </h4>
              <p
                style={{
                  fontSize: '0.9375rem',
                  color: '#57534E',
                  lineHeight: 1.6,
                  maxWidth: 500,
                  margin: '0 auto 28px',
                }}
              >
                Criminals use snippets of voice recordings from social media or spam calls to duplicate a child's voice. Take this risk assessment to check your family's vulnerability level.
              </p>
              <button
                onClick={handleStart}
                style={{
                  background: '#004322',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  padding: '14px 32px',
                  borderRadius: 9999,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
              >
                Begin Assessment
                <ArrowRight size={18} />
              </button>
            </motion.div>
          )}

          {currentStep >= 0 && currentStep < QUESTIONS.length && (
            <motion.div
              key={`question-${currentStep}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              style={{ width: '100%' }}
            >
              <h4
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.1875rem',
                  fontWeight: 600,
                  color: '#1C1917',
                  marginBottom: 24,
                  lineHeight: 1.4,
                }}
              >
                {QUESTIONS[currentStep].text}
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {QUESTIONS[currentStep].options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswerSelect(option.points)}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      borderRadius: 16,
                      background: 'white',
                      border: '1px solid #E7E5E4',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'start',
                      gap: 12,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.border = '1px solid #004322';
                      e.currentTarget.style.background = 'rgba(0, 67, 34, 0.01)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.border = '1px solid #E7E5E4';
                      e.currentTarget.style.background = 'white';
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: '2px solid #D6D3D1',
                        display: 'block',
                        marginTop: 2,
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <span style={{ fontWeight: 600, color: '#1C1917', fontSize: '0.9375rem', display: 'block' }}>
                        {option.label}
                      </span>
                      {option.description && (
                        <span style={{ color: '#57534E', fontSize: '0.8125rem', display: 'block', marginTop: 4, lineHeight: 1.4 }}>
                          {option.description}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {currentStep === QUESTIONS.length && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ width: '100%' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 24,
                  textAlign: 'center',
                }}
              >
                {/* Radial Gauge */}
                <div style={{ position: 'relative', width: 148, height: 148 }}>
                  <svg width={148} height={148} className="-rotate-90">
                    <circle
                      cx={74}
                      cy={74}
                      r={radius}
                      fill="none"
                      stroke="#F5F5F4"
                      strokeWidth={10}
                    />
                    <motion.circle
                      cx={74}
                      cy={74}
                      r={radius}
                      fill="none"
                      stroke={risk.color}
                      strokeWidth={10}
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      transition={{ duration: 1.2, ease: 'easeOut' }}
                    />
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '2.125rem',
                        fontWeight: 700,
                        color: risk.color,
                        lineHeight: 1,
                      }}
                    >
                      {displayScore}%
                    </span>
                    <span style={{ fontSize: '0.625rem', fontWeight: 700, color: '#A8A29E', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
                      Vulnerability
                    </span>
                  </div>
                </div>

                {/* Score Level Callout */}
                <div>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: risk.color,
                      background: risk.bg,
                      border: `1px solid ${risk.border}`,
                      padding: '4px 14px',
                      borderRadius: 999,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    {risk.level}
                  </span>
                  <h4
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#1C1917',
                      marginTop: 14,
                      marginBottom: 8,
                    }}
                  >
                    Vulnerability Assessment Complete
                  </h4>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: '#57534E',
                      lineHeight: 1.6,
                      maxWidth: 480,
                      margin: '0 auto',
                    }}
                  >
                    {risk.description}
                  </p>
                </div>

                {/* Recommendation steps */}
                <div
                  style={{
                    background: '#FAFAF7',
                    border: '1px solid #E7E5E4',
                    borderRadius: 16,
                    padding: 20,
                    width: '100%',
                    textAlign: 'left',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.01)',
                  }}
                >
                  <h5 style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1C1917', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShieldCheck size={16} className="text-[#004322]" /> Recommended Action Plan:
                  </h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      "Establish a secret family code word that cannot be researched online.",
                      "Discuss the 'emergency clone call' scam structure with parents.",
                      "Deploy a safe, educational Trustbox simulation call to verify their panic response."
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'start' }}>
                        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#E0F2FE', color: '#0284C7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.625rem', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                          {i+1}
                        </span>
                        <span style={{ fontSize: '0.8125rem', color: '#57534E', lineHeight: 1.4 }}>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, width: '100%' }}>
                  <button
                    onClick={handleReset}
                    style={{
                      background: 'white',
                      border: '1px solid #D6D3D1',
                      color: '#57534E',
                      fontWeight: 600,
                      padding: '12px 20px',
                      borderRadius: 9999,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.875rem',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#FAFAF7'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
                  >
                    <RotateCcw size={14} />
                    Retake Quiz
                  </button>
                  <Link
                    href="/dashboard/simulate"
                    onClick={() =>
                      trackEvent('landing_cta_clicked', {
                        ctaLabel: 'quiz_results_run_simulation',
                        destination: '/dashboard/simulate',
                      })
                    }
                    style={{
                      background: '#004322',
                      color: 'white',
                      fontWeight: 600,
                      padding: '12px 28px',
                      borderRadius: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                  >
                    Test Your Family Now
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
